import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function OgloszenieDodajPage() {
    const navigate = useNavigate();
    const [labels, setLabels] = useState({});
    const [categories, setCategories] = useState([]);
    const [captcha, setCaptcha] = useState({ question: "", token: "" });
    const [loadTs, setLoadTs] = useState(Date.now());
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        title: "", content: "", category: "",
        contact_name: "", contact_email: "", contact_phone: "",
        captcha_answer: "", website: "",
    });

    const fetchCaptcha = () => {
        api.get("/captcha").then((r) => {
            setCaptcha({ question: r.data.question, token: r.data.token });
            setLoadTs(Date.now());
        }).catch(() => {});
    };

    useEffect(() => {
        document.title = "Dodaj ogłoszenie – Dąbrówka Wiślana";
        api.get("/listings/categories").then((r) => {
            const cats = r.data?.categories || [];
            setCategories(cats);
            setLabels(r.data?.labels || {});
            setForm((f) => f.category ? f : { ...f, category: cats[0] || "" });
        }).catch(() => {});
        fetchCaptcha();
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (Date.now() - loadTs < 3000) {
            toast.error("Zbyt szybko – sprawdzamy, że nie jesteś botem.");
            return;
        }
        if (!form.contact_email && !form.contact_phone) {
            toast.error("Podaj przynajmniej e-mail lub telefon kontaktowy.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/listings", {
                title: form.title.trim(),
                content: form.content.trim(),
                category: form.category,
                contact_name: form.contact_name.trim(),
                contact_email: form.contact_email.trim(),
                contact_phone: form.contact_phone.trim(),
                captcha_token: captcha.token,
                captcha_answer: parseInt(form.captcha_answer, 10) || 0,
                website: form.website,
            });
            toast.success("Ogłoszenie zostało wysłane do moderatora. Pojawi się po zatwierdzeniu.");
            setTimeout(() => navigate("/ogloszenia"), 1500);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
            fetchCaptcha();
            setForm((f) => ({ ...f, captcha_answer: "" }));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-[#FAF8F3]" data-testid="ogloszenie-add-page">
            <Navbar />
            <main className="max-w-2xl mx-auto px-6 lg:px-10 py-12 pt-32">
                <Link to="/ogloszenia" className="inline-flex items-center gap-2 text-sm text-[#0D4F3C] hover:text-[#D4A537] mb-6">
                    <ArrowLeft size={14} /> Wróć do ogłoszeń
                </Link>

                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7 md:p-10">
                    <h1 className="font-serif text-3xl md:text-4xl text-[#092E22] mb-2">Dodaj nowe ogłoszenie</h1>
                    <p className="text-sm text-gray-500 mb-7">
                        Wypełnij formularz – ogłoszenie pojawi się publicznie po akceptacji moderatora.
                        Ogłoszenia są automatycznie archiwizowane po 30 dniach.
                    </p>

                    <form onSubmit={submit} className="space-y-4" data-testid="ogloszenie-form">
                        {/* honeypot */}
                        <input
                            type="text" name="website" tabIndex={-1} autoComplete="off"
                            value={form.website} onChange={set("website")}
                            style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }} aria-hidden="true"
                        />
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-[#092E22]">Kategoria *</label>
                            <select
                                required
                                value={form.category}
                                onChange={set("category")}
                                data-testid="ogloszenie-category"
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#0D4F3C]"
                            >
                                {categories.map((k) => (
                                    <option key={k} value={k}>{labels[k] || k}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-[#092E22]">Tytuł ogłoszenia *</label>
                            <input
                                required minLength={3} maxLength={120}
                                value={form.title} onChange={set("title")}
                                placeholder="Krótki, opisowy tytuł"
                                data-testid="ogloszenie-title"
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#0D4F3C]"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-[#092E22]">Treść *</label>
                            <textarea
                                required minLength={5} maxLength={3000} rows={5}
                                value={form.content} onChange={set("content")}
                                placeholder="Szczegółowy opis (stan, cena, warunki, lokalizacja...)"
                                data-testid="ogloszenie-content"
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#0D4F3C] resize-y"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-[#092E22]">Imię i nazwisko *</label>
                            <input
                                required minLength={2} maxLength={80}
                                value={form.contact_name} onChange={set("contact_name")}
                                data-testid="ogloszenie-contact-name"
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#0D4F3C]"
                            />
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-[#092E22]">E-mail kontaktowy</label>
                                <input
                                    type="email"
                                    value={form.contact_email} onChange={set("contact_email")}
                                    placeholder="kontakt@przyklad.pl"
                                    data-testid="ogloszenie-contact-email"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#0D4F3C]"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-[#092E22]">Telefon kontaktowy</label>
                                <input
                                    value={form.contact_phone} onChange={set("contact_phone")}
                                    placeholder="+48 ..."
                                    data-testid="ogloszenie-contact-phone"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#0D4F3C]"
                                />
                            </div>
                        </div>
                        <p className="text-[11px] text-gray-500">Podaj przynajmniej jedną formę kontaktu – e-mail lub telefon.</p>

                        <div className="flex items-center gap-3 pt-2">
                            <span className="text-sm text-gray-600 whitespace-nowrap">{captcha.question || "…"}</span>
                            <input
                                required type="number"
                                value={form.captcha_answer} onChange={set("captcha_answer")}
                                data-testid="ogloszenie-captcha"
                                className="w-20 px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-center focus:outline-none focus:border-[#0D4F3C]"
                            />
                            <button type="button" onClick={fetchCaptcha} title="Nowe pytanie" className="text-gray-400 hover:text-[#0D4F3C]" data-testid="ogloszenie-captcha-refresh">
                                <RefreshCw size={14} />
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="ogloszenie-submit"
                            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white font-medium disabled:opacity-60"
                        >
                            <Send size={14} /> {submitting ? "Wysyłanie..." : "Wyślij ogłoszenie do moderacji"}
                        </button>
                    </form>
                </motion.div>
            </main>
            <Footer />
        </div>
    );
}
